import { inject } from '@angular/core';
import { of, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { delay, retry, tap } from 'rxjs/operators';
import { HttpInterceptorFn } from '@angular/common/http';

import { DOOD_API } from '@config/ws.config';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { MarketplaceService } from '@core/services/marketplace/marketplace.service';
import { NoConnectionModalService } from '@core/services/no-connection-modal/no-connection-modal.service';
import { AnalyticsService } from '@core/services/app/analytics.service';

export const retryCount = 20;
export const baseRetryDelaySeconds = 2;
export const maxRetryDelaySeconds = 64;

/**
 * Calculate exponential backoff delay with maximum cap
 * @param attemptNumber - Current retry attempt (0-based)
 * @returns Delay in milliseconds
 */
function calculateExponentialDelay(attemptNumber: number): number {
  const delaySeconds = Math.min(
    baseRetryDelaySeconds * Math.pow(2, attemptNumber),
    maxRetryDelaySeconds,
  );
  return delaySeconds * 1000; // Convert to milliseconds
}

export const doodApiInterceptor: HttpInterceptorFn = (req, next) => {
  // Epson ePOS printer API doesn't allow custom header
  if (req.url.includes('cgi-bin/epos/service.cgi')) {
    return next(req);
  }

  const authSelector = inject(AuthStoreSelector);
  const translationService = inject(TranslateService);
  const marketplaceService = inject(MarketplaceService);
  const noConnectionModalService = inject(NoConnectionModalService);
  const analyticsService = inject(AnalyticsService);

  const analyticsSessionId = analyticsService.getAnalyticsSessionId();
  const analyticsAnonymousId = analyticsService.getAnalyticsAnonymousId();

  // Get current language synchronously
  const language =
    translationService.getCurrentLang() || translationService.getFallbackLang() || 'fr';

  // Get Firebase token reactively, wait for it to be available if needed

  let request = req.clone({
    setHeaders: {
      'Firebase-tenant': marketplaceService?.getMarketplaceTenantId() ?? 'DEFAULT-TENANT',
      'X-Analytics-Session-Id': analyticsSessionId ?? '',
      'X-Analytics-Anonymous-Id': analyticsAnonymousId ?? '',
    },
  });

  if (request.method === 'PATCH') {
    request = request.clone({
      setHeaders: {
        'Content-Type': 'application/merge-patch+json',
      },
    });
  }

  if (request.url.startsWith(DOOD_API)) {
    // POST /users/anonymous must not send Authorization (Dood JWT cannot create guests).
    const isCreateAnonymousUser =
      request.method === 'POST' && request.url.includes('/users/anonymous');

    const token = authSelector.selectStatusTokenSignal();
    const authorizationHeader =
      !isCreateAnonymousUser && token
        ? token.startsWith('Bearer ')
          ? token
          : `Bearer ${token}`
        : '';

    request = request.clone({
      setHeaders: {
        ...(authorizationHeader ? { Authorization: authorizationHeader } : {}),
        'X-locale': language,
      },
    });
  }

  request = request.clone({
    setHeaders: {
      'ngsw-bypass': 'true',
    },
  });

  return next(request).pipe(
    retry({
      delay: (error: any, count) => {
        analyticsService.log({
          level: 'error',
          label: 'DoodApiInterceptor.RequestError',
          message: {
            url: request.url,
            message: error?.message || error?.toString(),
          },
        });

        if (!request.url.startsWith(DOOD_API)) {
          return throwError(() => error);
        }

        if (count <= retryCount && error.status === 0) {
          if (count >= 1) {
            noConnectionModalService.setDisplay(true);
          }
          noConnectionModalService.setRetryInProgress(true);
          return of(error).pipe(delay(calculateExponentialDelay(count)));
        }
        if (count > retryCount && error.status === 0) {
          noConnectionModalService.setRetryInProgress(false);
        }
        return throwError(() => error);
      },
    }),
    tap(event => {
      if (event?.type === 0) {
        return;
      }
      noConnectionModalService.setDisplay(false);
      noConnectionModalService.setRetryInProgress(false);
    }),
  );
};
